import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Chip } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type BalanceCheckBarProps = {
    balanced: boolean;
    /**
     * Both sides' labels come from the **statement's own root total lines**, so
     * the bar always says what the rows above it say — including after a re-seed
     * that renames or reorders them.
     */
    leftLabel: string;
    rightLabel: string;
    /** Pre-formatted `₾` strings — no arithmetic in the render layer. */
    leftText: string;
    rightText: string;
    differenceText: string;
};

const Figure: React.FC<{ label: string; value: string; slot: string }> = ({
    label,
    value,
    slot,
}) => (
    <div className="flex flex-col items-start">
        <span className="text-xs font-medium text-muted">{label}</span>
        <span
            className="text-base font-semibold tabular-nums text-foreground"
            {...getNodeTestIdProp(`balance-sheet-check-${slot}`)}
        >
            {value}
        </span>
    </div>
);

/**
 * The Balance Sheet balance check — the statement's two root totals side by side.
 * Same visual language as the journal entry form's balance indicator: a calm
 * "Balanced" or the explicit gap. The state is conveyed by text + icon, never by
 * colour alone.
 *
 * The figures are the server's; the comparison is a comparison, not a
 * recomputation. The server's own verdict on the same two numbers arrives as the
 * `ASSETS_NE_EQLIAB` plausibility advisory.
 */
export const BalanceCheckBar: React.FC<BalanceCheckBarProps> = ({
    balanced,
    leftLabel,
    rightLabel,
    leftText,
    rightText,
    differenceText,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl bg-surface-secondary px-4 py-3">
            <div className="flex flex-wrap items-center gap-8">
                <Figure label={leftLabel} slot="assets" value={leftText} />
                <Figure
                    label={rightLabel}
                    slot="equity-liabilities"
                    value={rightText}
                />
            </div>

            <Chip
                color={balanced ? 'success' : 'danger'}
                variant="soft"
                {...getNodeTestIdProp('balance-sheet-balance-indicator')}
            >
                <Icon
                    aria-hidden
                    icon={
                        balanced
                            ? 'gravity-ui:circle-check'
                            : 'gravity-ui:scales'
                    }
                />
                <Chip.Label>
                    {balanced
                        ? t('statements.balanceSheet.balanced')
                        : t('statements.balanceSheet.offBy', {
                              amount: differenceText,
                          })}
                </Chip.Label>
            </Chip>
        </div>
    );
};
